import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Target, Check } from "lucide-react";
import { toast } from "sonner";
import { ConfirmChangeDialog } from "@/components/ConfirmChangeDialog";

const ALLOWED = ["2.5", "3.0", "3.5", "4.0", "4.5"];
const LABELS = { "2.5": "Beginner", "3.0": "Recreational", "3.5": "Intermediate", "4.0": "Strong Int.", "4.5": "Advanced" };

const LevelPicker = ({ value, onPick, testid, hint }) => (
  <div className="flex flex-wrap gap-2">
    {ALLOWED.map((lv) => {
      const on = value === lv;
      return (
        <button key={lv} type="button" onClick={() => onPick(lv)} data-testid={`${testid}-${lv}`}
          className="px-3 py-2 text-xs uppercase tracking-wider border transition-colors"
          style={on ? { borderColor: "#CCFF00", background: "rgba(204,255,0,0.08)", color: "#CCFF00" } : { borderColor: "#262626", color: "#E5E5E5" }}>
          <div className="font-bold">{lv}</div>
          <div className="text-[9px] text-[#A3A3A3] mt-0.5">{LABELS[lv]}{hint === lv ? " · self" : ""}</div>
        </button>
      );
    })}
  </div>
);

export const SetTargetLevelForm = ({ studentId, playerName, al, workingLevel, onSaved, highlight }) => {
  const [target, setTarget] = useState(al?.current || "");
  const [working, setWorking] = useState(workingLevel || al?.self_rated || "");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(!al?.current || highlight || window.location.hash === "#assessment");
  const [confirming, setConfirming] = useState(false);
  const below = target ? ALLOWED.slice(0, ALLOWED.indexOf(target)) : [];
  if (!open) {
    return (
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap border border-[#262626] p-3" data-testid="set-target-level-collapsed">
        <div className="text-xs text-[#A3A3A3]">Target level <span className="text-white font-semibold">{al.current}</span> · working level <span className="text-white font-semibold">{workingLevel || "—"}</span></div>
        <button type="button" onClick={() => setOpen(true)} className="text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black inline-flex items-center gap-1" data-testid="open-set-target-level">
          <Target className="w-3 h-3" /> Change target level
        </button>
      </div>
    );
  }

  const save = async () => {
    if (!target) { toast.error("Pick a target level first"); return; }
    setBusy(true);
    try {
      await api.post(`/coach/players/${studentId}/set-target-level`, { level: target, working_level: working || undefined });
      toast.success(`Target level set to ${target}${below.length ? ` · ${below.join(", ")} marked completed` : ""}`);
      if (al?.current) setOpen(false);
      setConfirming(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't set the target level");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`mb-6 p-4 border-l-4 ${highlight ? "border-[#FF9F1C] bg-[#FF9F1C]/5" : "border-[#CCFF00] bg-[#CCFF00]/5"}`} data-testid="set-target-level-form">
      <div className="flex items-start gap-3 mb-3">
        <Target className="w-5 h-5 text-[#CCFF00] shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="label-mono mb-1" style={{ color: highlight ? "#FF9F1C" : "#CCFF00" }}>
            {highlight ? "TARGET LEVEL NOT YET SET" : "SET TARGET LEVEL"}
          </div>
          <div className="text-sm text-[#E5E5E5]" data-testid="set-target-explainer">
            This is the level this player will work towards in Rally Lab. All previous levels will be marked as completed.
          </div>
        </div>
      </div>
      <div className="label-mono text-[10px] text-[#A3A3A3] mb-2">TARGET LEVEL (RALLY LAB)</div>
      <LevelPicker value={target} onPick={setTarget} testid="target-pick" />
      <div className="label-mono text-[10px] text-[#A3A3A3] mt-4 mb-2">WORKING LEVEL (WHERE {(playerName || "THE PLAYER").toUpperCase()} IS NOW)</div>
      <LevelPicker value={working} onPick={setWorking} testid="working-pick" hint={al?.self_rated} />
      {target && (
        <div className="mt-3 text-xs text-[#A3A3A3]" data-testid="set-target-preview">
          {below.length ? <>Will mark <span className="text-white">{below.join(", ")}</span> as completed. </> : "No lower levels to complete. "}
          Saving completes the assessment and unlocks booking.
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => (target ? setConfirming(true) : toast.error("Pick a target level first"))} disabled={busy || !target} className="btn-volt px-5 py-2.5 text-xs disabled:opacity-50" data-testid="set-target-level-save">
          <Check className="w-4 h-4 inline mr-1" /> {busy ? "Saving…" : target ? `Set ${target} as target level` : "Pick a target level"}
        </button>
        {al?.current && (
          <button type="button" onClick={() => setOpen(false)} className="px-4 py-2.5 text-xs uppercase tracking-wider border border-[#262626] text-[#A3A3A3] hover:text-white" data-testid="set-target-level-cancel">
            Cancel
          </button>
        )}
      </div>
      <ConfirmChangeDialog open={confirming} busy={busy} testid="set-target" confirmLabel={`Set ${target}`}
        title="Confirm target level"
        lines={[
          al?.current && al.current !== target
            ? `You are changing ${playerName || "this player"}'s target level from ${al.current} to ${target}${ALLOWED.indexOf(target) < ALLOWED.indexOf(al.current) ? " — this is a downgrade" : ""}.`
            : `You are setting ${playerName || "this player"}'s target level to ${target}.`,
          below.length ? `All levels below ${target} (${below.join(", ")}) will be marked completed.` : "There are no lower levels to complete.",
          working && working !== (workingLevel || "") ? `Working level will be set to ${working}.` : null,
          "This completes the assessment and unlocks booking.",
        ].filter(Boolean)}
        onConfirm={save} onCancel={() => setConfirming(false)} />
    </div>
  );
};
